import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Cpu, Brain, Users, Rocket, ArrowRight, Check, Globe, ChevronDown, FlaskConical, HeartPulse, CircuitBoard, Radio, Zap, Landmark, ShoppingBag, Building2 } from 'lucide-react';
import SEO from '../components/SEO';
import {
  companyInfo,
  stats,
  servicePillars,
  whyTekClapSnapshot,
  regions,
  images,
} from '../data/mock';

const iconMap = {
  Cpu: Cpu,
  Brain: Brain,
  Users: Users,
  Rocket: Rocket,
};

const industries = [
  {
    name: 'Life Sciences',
    description: 'Accelerating R&D, clinical operations, and compliance with intelligent platforms.',
    icon: FlaskConical,
  },
  {
    name: 'Healthcare',
    description: 'Modernizing care delivery, patient experience, and health data ecosystems.',
    icon: HeartPulse,
  },
  {
    name: 'Semiconductors',
    description: 'Powering design, manufacturing, and supply chain intelligence at scale.',
    icon: CircuitBoard,
  },
  {
    name: 'Telecom & Media',
    description: 'Enabling next-gen networks, content platforms, and customer engagement.',
    icon: Radio,
  },
  {
    name: 'Energy & Utilities',
    description: 'Driving sustainable operations, grid intelligence, and asset optimization.',
    icon: Zap,
  },
  {
    name: 'BFSI',
    description: 'Transforming banking, insurance, and capital markets with secure innovation.',
    icon: Landmark,
  },
  {
    name: 'CPG',
    description: 'Unlocking consumer insight, omnichannel commerce, and supply resilience.',
    icon: ShoppingBag,
  },
  {
    name: 'Public Sector / Government',
    description: 'Delivering citizen-centric digital services with trust and transparency.',
    icon: Building2,
  },
];

const HomePage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setIsVisible(true);
    
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 20;
      setMousePosition({ x, y });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight - 80, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title={null}
        description="TekClap is a technology, AI, and product consulting firm helping organizations modernize platforms, build intelligent products, and scale teams effectively."
        keywords="technology consulting, AI consulting, product development, workforce solutions, digital transformation, talent solutions"
      />
      
      {/* Hero Section - Full Viewport Height */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-20 md:pt-28 lg:pt-32">
        {/* Animated Background with Parallax Effect */}
        <div className="absolute inset-0 z-0">
          <img
            src={images.hero}
            alt="Technology AI Product Consulting"
            className="w-full h-full object-cover transition-transform duration-300 ease-out"
            style={{
              transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px) scale(1.1)`
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/60" />
          
          {/* Animated Gradient Orbs */}
          <div 
            className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#F58B23]/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDuration: '4s' }}
          />
          <div 
            className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-[#F58B23]/5 rounded-full blur-3xl animate-pulse"
            style={{ animationDuration: '6s', animationDelay: '2s' }}
          />
        </div>
        
        {/* Floating Particles Effect */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-[#F58B23]/30 rounded-full animate-float"
              style={{
                left: `${15 + i * 15}%`,
                top: `${20 + (i % 3) * 25}%`,
                animationDelay: `${i * 0.5}s`,
                animationDuration: `${3 + i * 0.5}s`
              }}
            />
          ))}
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            {/* Main Headline with Staggered Animation */}
            <h1 
              className={`text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-[#1A1A1A] mb-6 leading-tight transition-all duration-700 delay-100 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <span className="block">Empowering</span>
              <span className="block text-[#F58B23]">Innovation</span>
              <span className="block">Through Technology & Talent</span>
            </h1>
            
            <p 
              className={`text-lg md:text-xl text-[#6B7280] mb-10 max-w-xl transition-all duration-700 delay-200 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              We help organizations modernize technology platforms, build AI-powered products, 
              develop intelligent solutions, and scale their workforce effectively.
            </p>
            
            {/* CTA Buttons */}
            <div 
              className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-300 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-[#F58B23] hover:bg-[#D97706] text-white font-medium px-8 py-6 text-base group transition-all hover:shadow-lg hover:shadow-[#F58B23]/25"
                >
                  Schedule Consultation
                  <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/services">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white font-medium px-8 py-6 text-base transition-all"
                >
                  Explore Capabilities
                </Button>
              </Link>
            </div>
            
            {/* Stats Preview */}
            <div 
              className={`flex gap-8 mt-12 pt-8 border-t border-gray-200 pb-16 transition-all duration-700 delay-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {stats.slice(0, 3).map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl md:text-3xl font-serif font-bold text-[#1A1A1A]">
                    {stat.value}
                  </div>
                  <div className="text-xs text-[#6B7280] uppercase tracking-wider">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <button 
          onClick={scrollToContent}
          className={`absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#6B7280] hover:text-[#F58B23] transition-all cursor-pointer ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDelay: '800ms' }}
        >
          <span className="text-xs uppercase tracking-wider">Explore</span>
          <ChevronDown className="w-5 h-5 animate-bounce" />
        </button>
      </section>

      {/* Three Pillars Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1A1A1A] mb-4">
              Our Strategic Pillars
            </h2>
            <p className="text-[#6B7280] max-w-2xl mx-auto">
              Four integrated practice areas designed to drive your business forward.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {servicePillars.map((pillar, index) => {
              const IconComponent = iconMap[pillar.icon];
              return (
                <Link
                  key={pillar.id}
                  to={pillar.link}
                  className={`group card-hover bg-[#FAFAF8] rounded-2xl overflow-hidden border border-gray-100 animate-fade-in-up`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Image */}
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={pillar.image}
                      alt={pillar.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                        <IconComponent className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>
                  {/* Content */}
                  <div className="p-5">
                    <h3 className="text-lg font-serif font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                      {pillar.title}
                    </h3>
                    <p className="text-[#6B7280] text-sm leading-relaxed mb-3">
                      {pillar.description}
                    </p>
                    <span className="inline-flex items-center text-[#F58B23] font-medium text-sm group-hover:gap-2 transition-all">
                      Learn more
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries We Serve Section */}
      <section id="industries" className="py-20 bg-[#FAFAF8] scroll-mt-32" data-testid="industries-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-[#F58B23] font-medium text-sm uppercase tracking-wider mb-4 block">
              Industry Expertise
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1A1A1A] mb-4">
              Industries We Serve
            </h2>
            <p className="text-[#6B7280] max-w-2xl mx-auto">
              Deep domain expertise across regulated, data-intensive, and rapidly evolving industries.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div
                  key={industry.name}
                  data-testid={`industry-card-${industry.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                  className="group relative bg-white rounded-2xl p-6 border border-gray-100 hover:border-[#F58B23]/30 hover:shadow-lg hover:shadow-[#F58B23]/5 transition-all duration-300 animate-fade-in-up"
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#FFF4E6] flex items-center justify-center mb-4 group-hover:bg-[#F58B23] transition-colors duration-300">
                    <Icon className="w-6 h-6 text-[#F58B23] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-lg font-serif font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#F58B23] transition-colors">
                    {industry.name}
                  </h3>
                  <p className="text-[#6B7280] text-sm leading-relaxed">
                    {industry.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Proof Bar Section */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-serif font-bold text-[#F58B23] mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-400 text-sm uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
            <div className="text-center col-span-2 md:col-span-1">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Globe className="w-6 h-6 text-[#F58B23]" />
                <span className="text-2xl font-serif font-bold text-white">Global</span>
              </div>
              <div className="text-gray-400 text-sm">
                {regions.join(' • ')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why TekClap Snapshot */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#F58B23] font-medium text-sm uppercase tracking-wider mb-4 block">
                Why Choose Us
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1A1A1A] mb-6">
                The TekClap Difference
              </h2>
              <p className="text-[#6B7280] mb-8 leading-relaxed">
                We combine deep technical expertise with genuine understanding of your business challenges to deliver solutions that truly matter.
              </p>
              <Link to="/why-tekclap">
                <Button
                  variant="outline"
                  className="border-[#F58B23] text-[#F58B23] hover:bg-[#F58B23] hover:text-white"
                >
                  Discover More
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="space-y-6">
              {whyTekClapSnapshot.map((item, index) => (
                <div
                  key={index}
                  className="flex gap-4 p-6 bg-[#FAFAF8] rounded-xl border border-gray-100"
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-[#FFF4E6] flex items-center justify-center">
                      <Check className="w-5 h-5 text-[#F58B23]" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1A1A1A] mb-1">
                      {item.title}
                    </h4>
                    <p className="text-[#6B7280] text-sm">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with Background Image */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={images.partnership}
            alt="Partnership"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1A1A1A]/85" />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-gray-300 mb-8 text-lg">
              Let's discuss how TekClap can help you achieve your technology and talent goals.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#F58B23] hover:bg-[#D97706] text-white font-medium px-10 py-6 text-base"
              >
                Start the Conversation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
